/**
 * 🎯 SHARED: Structured Data Composable (JSON-LD)
 *
 * Generiert Schema.org Markup für bessere SEO mit dynamischer Projekt-Konfiguration
 * Verwendet useProjectConfig() für automatische Restaurant-Daten
 */
import { onMounted, onUnmounted } from 'vue';
import { useProjectConfig } from './useProjectConfig';
/**
 * Generiert Restaurant Schema.org Markup
 */
function generateRestaurantSchema(config) {
    const restaurant = config.restaurant;
    const domain = config.deployment.domains.production;
    return {
        "@context": "https://schema.org",
        "@type": "Restaurant",
        "@id": `https://${domain}/#restaurant`,
        "name": restaurant.name,
        "alternateName": restaurant.shortName,
        "description": restaurant.description,
        "image": `https://${domain}/og-image.jpg`,
        "logo": `https://${domain}/favicon.ico`,
        // Adresse
        "address": {
            "@type": "PostalAddress",
            "streetAddress": `${restaurant.address.street} ${restaurant.address.number}`,
            "postalCode": restaurant.address.postalCode,
            "addressLocality": restaurant.address.city,
            "addressRegion": restaurant.address.district || restaurant.address.state || "",
            "addressCountry": restaurant.address.country
        },
        // Kontaktdaten
        "telephone": restaurant.contact.phone,
        "email": restaurant.contact.email,
        "url": restaurant.contact.website || `https://${domain}`,
        // Öffnungszeiten (OpeningHoursSpecification)
        "openingHoursSpecification": [
            {
                "@type": "OpeningHoursSpecification",
                "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday"],
                "opens": restaurant.openingHours.monday?.open || "11:00",
                "closes": restaurant.openingHours.monday?.close || "22:00"
            },
            {
                "@type": "OpeningHoursSpecification",
                "dayOfWeek": ["Friday", "Saturday"],
                "opens": restaurant.openingHours.friday?.open || "11:00",
                "closes": restaurant.openingHours.friday?.close || "23:00"
            },
            {
                "@type": "OpeningHoursSpecification",
                "dayOfWeek": "Sunday",
                "opens": restaurant.openingHours.sunday?.open || "12:00",
                "closes": restaurant.openingHours.sunday?.close || "22:00"
            }
        ],
        // Küche & Preisklasse
        "servesCuisine": restaurant.cuisine.join(", "),
        "priceRange": restaurant.priceRange || "€€",
        // Zahlungsmethoden
        "paymentAccepted": "Cash, Credit Card, PayPal, EC Card",
        // Lieferservice
        "hasDeliveryMethod": {
            "@type": "DeliveryMethod",
            "name": "Delivery Service"
        },
        // Reservierungen
        "acceptsReservations": "true",
        // Social Media (optional)
        ...(restaurant.social?.instagram && {
            "sameAs": [
                restaurant.social.instagram,
                restaurant.social.facebook
            ].filter(Boolean)
        }),
        // Geo-Koordinaten (optional - wenn in config vorhanden)
        ...(restaurant.location?.latitude && restaurant.location?.longitude && {
            "geo": {
                "@type": "GeoCoordinates",
                "latitude": restaurant.location.latitude,
                "longitude": restaurant.location.longitude
            }
        })
    };
}
/**
 * Generiert MenuItem Schema für Produkte
 */
function generateMenuItemSchema(item, domain) {
    return {
        "@context": "https://schema.org",
        "@type": "MenuItem",
        "@id": `https://${domain}/speisekarte/${item.slug}`,
        "name": item.name,
        "description": item.description,
        "image": item.image,
        "offers": {
            "@type": "Offer",
            "price": (item.priceCent / 100).toFixed(2),
            "priceCurrency": "EUR",
            "availability": "https://schema.org/InStock"
        },
        "nutrition": item.allergens ? {
            "@type": "NutritionInformation",
            "allergenWarning": item.allergens.join(", ")
        } : undefined
    };
}
/**
 * Generiert BreadcrumbList für Navigation
 */
function generateBreadcrumbSchema(breadcrumbs, domain) {
    return {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": breadcrumbs.map((crumb, index) => ({
            "@type": "ListItem",
            "position": index + 1,
            "name": crumb.name,
            "item": `https://${domain}${crumb.url}`
        }))
    };
}
/**
 * Generiert Organization Schema für Footer
 */
function generateOrganizationSchema(config) {
    const restaurant = config.restaurant;
    const domain = config.deployment.domains.production;
    return {
        "@context": "https://schema.org",
        "@type": "Organization",
        "@id": `https://${domain}/#organization`,
        "name": restaurant.name,
        "url": `https://${domain}`,
        "logo": `https://${domain}/favicon.ico`,
        "contactPoint": {
            "@type": "ContactPoint",
            "telephone": restaurant.contact.phone,
            "contactType": "customer service",
            "email": restaurant.contact.email,
            "availableLanguage": ["de", "en"]
        }
    };
}
/**
 * Fügt Structured Data zum <head> hinzu
 */
function injectStructuredData(schema, id = 'structured-data') {
    if (typeof document === 'undefined')
        return;
    // Entferne existierendes Script (falls vorhanden)
    const existingScript = document.getElementById(id);
    if (existingScript) {
        existingScript.remove();
    }
    // Erstelle neues Script-Tag
    const script = document.createElement('script');
    script.id = id;
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(schema, null, 2);
    // Füge zum <head> hinzu
    document.head.appendChild(script);
}
/**
 * Main Composable für Structured Data mit dynamischer Konfiguration
 */
export function useStructuredData(options) {
    const { config } = useProjectConfig();
    let scriptId;
    onMounted(() => {
        const type = options?.type || 'Restaurant';
        scriptId = `structured-data-${type.toLowerCase()}`;
        let schema;
        const domain = config.deployment.domains.production;
        switch (type) {
            case 'Restaurant':
                schema = generateRestaurantSchema(config);
                break;
            case 'MenuItem':
                schema = generateMenuItemSchema(options?.data, domain);
                break;
            case 'BreadcrumbList':
                schema = generateBreadcrumbSchema(options?.data, domain);
                break;
            case 'Organization':
                schema = generateOrganizationSchema(config);
                break;
            default:
                schema = generateRestaurantSchema(config);
        }
        injectStructuredData(schema, scriptId);
        console.log(`📊 [${config.project.id}] Structured Data injected: ${type}`);
    });
    onUnmounted(() => {
        if (typeof document === 'undefined')
            return;
        // Cleanup beim Unmount
        const script = document.getElementById(scriptId);
        if (script) {
            script.remove();
        }
    });
    return {
        generateRestaurantSchema,
        generateMenuItemSchema,
        generateBreadcrumbSchema,
        generateOrganizationSchema,
        injectStructuredData
    };
}
// Export einzelner Generator-Funktionen für direkte Verwendung
export { generateRestaurantSchema, generateMenuItemSchema, generateBreadcrumbSchema, generateOrganizationSchema, injectStructuredData };
